import { useEffect, useState } from 'react';
import { Bell, PackagePlus } from 'lucide-react';
import { api, apiError } from '@/lib/pos-api';
import useNotificationBadge, { notificationToken } from '@/hooks/useNotificationBadge';
import { Button } from '@/components/ui/button';

function ProductNotifications({ scope, storeId, kind }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const stock = kind === 'stock';
  const title = stock ? 'Peringatan stok' : 'Rilis baru';
  const Icon = stock ? Bell : PackagePlus;
  const badge = useNotificationBadge(scope, kind, items.map(notificationToken));
  useEffect(() => {
    if (!storeId) return;
    let active = true, pending = false;
    const controller = new AbortController();
    const load = async () => {
      if (pending) return;
      pending = true;
      try {
        const {data} = await api.get('/product-notifications', {params:{limit:20}, headers:{'X-Store-Id':storeId}, signal:controller.signal});
        if (active) { setItems((data || []).filter(item => stock ? ['stock_empty','stock_low'].includes(item.kind) : item.kind === 'product_created')); setLoadError(''); }
      } catch (e) { if (active) setLoadError(apiError(e)); }
      finally { pending = false; if (active) setLoading(false); }
    };
    load(); const timer = window.setInterval(load, 60000);
    window.addEventListener('online', load);
    return () => { active = false; controller.abort(); window.clearInterval(timer); window.removeEventListener('online', load); };
  }, [storeId, stock]);
  return <div className="relative z-20">
    <button type="button" data-testid={`${kind}-notifications`} className="theme-shortcut relative" aria-label={title} title={title} aria-expanded={open} onClick={() => setOpen(v => !v)}>
      <Icon size={19}/>{badge.unread > 0 && <span data-testid={`${kind}-notification-count`} className={`absolute -right-1 -top-1 min-w-4 h-4 px-1 rounded-full ${stock ? 'bg-danger' : 'bg-brand-button'} text-white text-[10px] leading-4 text-center`}>{badge.unread > 9 ? '9+' : badge.unread}</span>}
    </button>
    {open && <section data-testid={`${kind}-notification-panel`} className="fixed left-2 right-2 top-24 sm:absolute sm:left-auto sm:right-0 sm:top-11 sm:w-[min(340px,calc(100vw-2rem))] rounded-xl border border-ui-line bg-ui-card shadow-xl p-3">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2"><strong className="text-sm">{title}</strong><div className="flex gap-2 items-center">
        <Button variant="outline" size="sm" data-testid={`${kind}-notifications-clear`} disabled={loading || !!loadError || !badge.unread} onClick={badge.clear}>Bersihkan</Button>
        <button type="button" className="text-xs text-brand-readable" onClick={() => setOpen(false)}>Tutup</button>
      </div></div>
      <p className="text-xs text-ui-muted mb-2">Bersihkan hanya menghilangkan angka. Daftar tetap tersedia.</p>
      {(badge.error || loadError) && <p role="alert" className="text-xs text-danger-readable mb-2">{badge.error || loadError}</p>}
      {loading ? <p className="text-xs py-3">Memuat…</p> : !items.length && !loadError ? <p className="text-xs text-ui-muted py-3">{stock ? 'Stok aman.' : 'Belum ada produk baru.'}</p> : <div className="max-h-72 overflow-auto space-y-1">{items.map(item => <div key={item.id} className={`rounded-lg px-3 py-2 ${stock ? item.kind === 'stock_empty' ? 'bg-danger-soft' : 'bg-warning-soft' : 'bg-ui-soft'}`}>
        <div className={`text-sm font-semibold break-words ${stock ? item.kind === 'stock_empty' ? 'text-danger-readable' : 'text-warning-readable' : ''}`}>{stock ? item.kind === 'stock_empty' ? 'Stok kosong · ' : 'Stok menipis · ' : ''}{item.name}</div>
        <div className="text-[11px] text-ui-muted">{stock ? 'Segera lakukan penerimaan atau pengisian stok' : `${item.by} · ${new Date(item.at).toLocaleString('id-ID')}`}</div>
      </div>)}</div>}
    </section>}
  </div>;
}

export const StockNotifications = props => <ProductNotifications {...props} kind="stock"/>;
export const ReleaseNotifications = props => <ProductNotifications {...props} kind="release"/>;
